import { JSONParser } from "@streamparser/json";
import _omit from "lodash-es/omit";
import { QUERY_OUTPUT } from "types/outputFormat";
import { isSetStatement } from "utils/helpers/Query/splitSQLQuery";
import { stripComments } from "utils/helpers/Query/strip-comments/strip";

import { FeatureFlag } from "featureFlags/constants";
import { useAllFlags } from "featureFlags/hooks/useAllFlags";
import { AuthService } from "services/auth";
import { WorkspaceEngine } from "services/engines/engine.types";
import { normalizeResponse } from "services/query/helpers";

import { NONE } from "pages/DevelopWorkspace/Editor/Document/DocumentContextSelector/types";
import {
  FIREBOLT_REMOVE_PARAMETER_HEADER,
  FIREBOLT_UPDATE_ENDPOINT_HEADER,
  FIREBOLT_UPDATE_PARAMETER_HEADER,
  SYSTEM_QUERY_PARAMETERS,
} from "pages/DevelopWorkspace/services/constants";
import { buildEngineUrl } from "pages/DevelopWorkspace/services/helpers/buildEngineUrl";
import { EmptyBodyError } from "pages/DevelopWorkspace/services/helpers/executionErrors/EmptyBodyError";
import getQueryLabel from "pages/DevelopWorkspace/services/helpers/getQueryLabel";
import getSystemParamErrorMessage from "pages/DevelopWorkspace/services/helpers/getSystemParamErrorMessage";
import { tryExtractJsonErrorDescription } from "pages/DevelopWorkspace/services/helpers/tryExtractJsonErrorDescription";
import { FetchOptions } from "pages/DevelopWorkspace/services/types";
import {
  ExecutionContext,
  ExecutionType,
  ExplainType,
  QueryStatement,
  WorkspaceDocument,
} from "pages/DevelopWorkspace/workspace.types";

import { parseQuerySetting } from "components/utils/queries";

import { ServerError } from "./executionErrors/ServerError";
import { TooLargeResponseError } from "./executionErrors/TooLargeResponseError";
import { isCancelMessage } from "./isCancelError";

const MAX_RESPONSE_SIZE_MB = 500;
const maxResponseSizeBytes = MAX_RESPONSE_SIZE_MB * 1024 * 1024;

type ExecutionUpdateParameters = {
  // list of params (flags, settings, etc.) that should be updated in document context
  engine?: string;
  database?: string;
  settings?: ExecutionContext["settings"];
  machineGeneratedSettings?: ExecutionContext["machineGeneratedSettings"];
};

const createUpdateParameters = ({
  fireboltUpdateParameters,
  fireboltUpdateEndpoint,
  sourceParams,
  flags,
  fireboltRemoveParameters,
}: {
  fireboltUpdateParameters: string;
  fireboltUpdateEndpoint: string;
  fireboltRemoveParameters: string;
  sourceParams: ExecutionUpdateParameters;
  flags?: ReturnType<typeof useAllFlags>;
}): ExecutionUpdateParameters => {
  const params = fireboltUpdateParameters
    ? fireboltUpdateParameters.split(", ")
    : [];
  const paramsObj: {
    [key: string]: string;
  } = params.reduce((acc, param) => {
    const [key, value] = param.split("=");
    return {
      ...acc,
      [key]: value,
    };
  }, {});

  const paramsToRemove = fireboltRemoveParameters
    ? fireboltRemoveParameters.split(", ")
    : [];

  const newParams = {
    ...sourceParams,
  };

  newParams.database = paramsObj.database;

  if (fireboltUpdateEndpoint) {
    const url = new URL("https://" + fireboltUpdateEndpoint);
    const searchParams = new URLSearchParams(url.search);
    const engine = searchParams.get("engine");

    if (engine) {
      newParams.engine = engine;
    }
  }

  if (flags?.[FeatureFlag.FireboltAppEnableTransactions]) {
    const machineGeneratedSettings = _omit(paramsObj, ["database", "engine"]);

    newParams.machineGeneratedSettings = {
      ...newParams.machineGeneratedSettings,
      ...machineGeneratedSettings,
    };
  }

  if (paramsToRemove.length) {
    paramsToRemove.forEach(param => {
      if (newParams.settings?.[param]) {
        delete newParams.settings[param];
      }
      if (newParams.machineGeneratedSettings?.[param]) {
        delete newParams.machineGeneratedSettings[param];
      }
    });
  }

  return newParams;
};

const processSetStatement = (
  query: string
): {
  content: string;
  key: string;
  value: string;
} => {
  const newSettings = parseQuerySetting(query);

  if (newSettings?.key && newSettings?.value) {
    if (SYSTEM_QUERY_PARAMETERS.includes(newSettings.key)) {
      throw new Error(getSystemParamErrorMessage(newSettings.key));
    }

    return {
      content: `-- Validation set ${newSettings.key} = ${newSettings.value}`,
      key: newSettings.key,
      value: newSettings.value,
    };
  }

  throw new Error("Invalid SET statement");
};

const executeQueryStatement = async (
  queryStatement: QueryStatement,
  document: WorkspaceDocument,
  engine: WorkspaceEngine,
  fetchOptions: FetchOptions,
  authService: AuthService,
  flags?: ReturnType<typeof useAllFlags>
): Promise<{
  responseBody: any;
  updateParameters: ExecutionUpdateParameters;
}> => {
  const isLite = Boolean(process.env.REACT_APP_LITE);
  const token = await authService.getToken();

  if (!token) {
    throw new Error("Auth token is not available");
  }

  const settings = { ...document.context.settings };
  const machineGeneratedSettings = {
    ...document.context.machineGeneratedSettings,
  };

  // list of params (flags, settings, etc.) that should be updated in document context
  const updateParameters: ExecutionUpdateParameters = {
    settings,
    ...(flags?.[FeatureFlag.FireboltAppEnableTransactions] && {
      machineGeneratedSettings,
    }),
  };

  let contentToExecute = queryStatement.content;

  if (isSetStatement(queryStatement.content)) {
    // SET statement processing function is not supporting comments, so we need to strip them
    const { content, key, value } = processSetStatement(
      stripComments(queryStatement.content)
    );

    contentToExecute = content;
    settings[key] = value;

    // if validation query executes successfully, the flag will be added to the settings
    updateParameters.settings = {
      ...settings,
    };
  }

  const getDatabase = () => {
    if (
      !document.context.database.name ||
      document.context.database.name === NONE
    ) {
      return "";
    }
    return document.context.database.name as string;
  };

  // params for current execution
  const queryParameters = {
    ...(flags?.[FeatureFlag.FireboltAppEnableTransactions] &&
      machineGeneratedSettings),
    ...settings,
    query_label: getQueryLabel(queryStatement, document),
    database: getDatabase(),
    output_format: isLite
      ? QUERY_OUTPUT.JSON_COMPACT
      : QUERY_OUTPUT.JSON_COMPACT_LIMITED,
    cancel_query_on_connection_drop: "type_dependent",
  };

  if (
    !isSetStatement(queryStatement.content) &&
    flags?.[FeatureFlag.FireboltAppEnableProactiveWarnings]
  ) {
    Object.assign(queryParameters, {
      append_performance_hints_to_json_output_format: true,
    });
  }

  if (
    !isSetStatement(queryStatement.content) &&
    (document.execution?.executionType === ExecutionType.Explain ||
      queryStatement.executionType === ExecutionType.Explain)
  ) {
    const type = queryStatement.explainType ?? ExplainType.Physical;
    contentToExecute = `explain (format json, ${type}) ${contentToExecute}`;
  } else if (flags && flags[FeatureFlag.FireboltAppEnableQueryProfile]) {
    Object.assign(queryParameters, {
      append_explain_analyze_to_json_output_format: true,
    });
  }

  const engineURL = new URL(`https://${engine.url}`);
  const url = buildEngineUrl(engineURL, queryParameters);

  const { headers, abortController } = fetchOptions;
  const response = await fetch(url, {
    signal: abortController.signal,
    method: "POST",
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
    },
    body: contentToExecute,
  });

  // extract firebolt update parameters when USE statement is used
  const fireboltUpdateParameters =
    response.headers.get(FIREBOLT_UPDATE_PARAMETER_HEADER) || "";

  const fireboltUpdateEndpoint =
    response.headers.get(FIREBOLT_UPDATE_ENDPOINT_HEADER) || "";

  const fireboltRemoveParameters =
    response.headers.get(FIREBOLT_REMOVE_PARAMETER_HEADER) || "";

  const newUpdateParams =
    fireboltUpdateParameters ||
    fireboltUpdateEndpoint ||
    fireboltRemoveParameters
      ? createUpdateParameters({
          fireboltUpdateParameters,
          fireboltUpdateEndpoint,
          sourceParams: updateParameters,
          fireboltRemoveParameters,
          flags,
        })
      : updateParameters;

  if (response.ok) {
    try {
      const parser = new JSONParser();

      let json: any;

      parser.onValue = parsedElementInfo => {
        if (!parsedElementInfo.parent) {
          json = parsedElementInfo.value;
        }
      };

      if (response.body) {
        let bytesReceived = 0;

        const reader = response.body.getReader();
        let done = false;

        while (!done) {
          // eslint-disable-next-line no-await-in-loop -- we need to wait for the next chunk
          const { done: isDone, value } = await reader.read();
          done = isDone;

          if (value) {
            bytesReceived += value.length;

            if (bytesReceived > maxResponseSizeBytes) {
              fetchOptions.abortController.abort();
              throw new TooLargeResponseError(
                `Query results exceed the ${MAX_RESPONSE_SIZE_MB}MB limit. Use the LIMIT clause to reduce the result size.`
              );
            }

            try {
              parser.write(value);
            } catch (e) {
              // JSON is not valid. (Probably is is a plain text response)
              console.error("Error parsing JSON response", e);
            }
          }
        }
      }

      if (json === undefined) {
        throw new Error(
          "Response is empty. Should be replaced with a placeholder."
        );
      }

      const errorMessage = json?.errors?.[0]?.description;

      if (errorMessage) {
        throw new ServerError(errorMessage);
      }

      if (json?.errors) {
        throw new Error("Unknown error occurred.");
      }

      const normalizedResponse = normalizeResponse({
        response: json,
        querySettings: queryParameters,
        responseStatusCode: response.status,
      });

      return {
        responseBody: normalizedResponse,
        updateParameters: newUpdateParams,
      };
    } catch (e: unknown) {
      if (e instanceof EmptyBodyError) {
        throw e;
      }

      if (e instanceof TooLargeResponseError) {
        throw e;
      }

      if (e instanceof ServerError) {
        const isCancel = isCancelMessage(e.message);
        if (!isCancel) {
          throw new Error(e.message);
        }

        const normalizedResponse = normalizeResponse({
          response: {
            meta: [
              {
                name: "No",
                type: "",
                displayName: "No",
              },
              {
                name: "",
                type: "string",
              },
            ],
            data: [[1, "Query execution has been canceled."]],
            statistics: {},
            errors: [{ description: e.message }],
          },
          querySettings: queryParameters,
          responseStatusCode: response.status,
        });

        return {
          responseBody: normalizedResponse,
          updateParameters: newUpdateParams,
        };
      }

      // mock response if it cannot be parsed
      const normalizedResponse = normalizeResponse({
        response: {
          meta: [],
          data: [],
          statistics: {},
        },
        querySettings: queryParameters,
        responseStatusCode: response.status,
      });

      return {
        responseBody: normalizedResponse,
        updateParameters: newUpdateParams,
      };
    }
  }

  try {
    const error = await response.clone().json();
    const errorMessage = error?.errors?.[0]?.description;
    throw new ServerError(errorMessage);
  } catch (e) {
    if (e instanceof ServerError) {
      throw new Error(e.message);
    }

    const text = await response.text();
    const normalized = tryExtractJsonErrorDescription(text);
    throw new Error(normalized || text);
  }
};

export default executeQueryStatement;
